export const formatCountdown = (diff: number) => {
  if (diff <= 0) return "00:00";
  const days = Math.floor(diff / (3600000 * 24));
  const remainingHours = Math.floor((diff % (3600000 * 24)) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);
  
  const mStr = mins.toString().padStart(2, '0');
  const sStr = secs.toString().padStart(2, '0');
  
  if (days > 0) {
    return `${days}d ${remainingHours.toString().padStart(2, '0')}:${mStr}:${sStr}`;
  } else if (remainingHours > 0) {
    return `${remainingHours.toString().padStart(2, '0')}:${mStr}:${sStr}`;
  } else {
    return `${mStr}:${sStr}`;
  }
};

const DAY_TIMEFRAMES = ['1s', '1m', '2m', '3m', '5m', '10m', '15m', '20m', '30m', '1h'];
const MONTH_TIMEFRAMES = ['2h', '4h', '6h', '8h', '12h', '1d', '2d', '3d', '1w'];

export const getTimeframeSeparation = (timeframe?: string): 'day' | 'week' | 'month' | 'year' | 'none' => {
  if (!timeframe) return 'none';
  // 1M = 1 Month (case sensitive!)
  if (timeframe === '1M') return 'year';
  
  const tf = timeframe.toLowerCase();
  if (DAY_TIMEFRAMES.includes(tf)) return 'day';
  if (MONTH_TIMEFRAMES.includes(tf)) return 'month';
  
  // Fallback for custom timeframes not explicitly mapped
  if (tf.endsWith('h') || tf.endsWith('d') || tf.endsWith('w')) return 'month';
  return 'none';
};

export const formatDateShortMonth = (date: Date, timeZone?: string) => {
  const day = date.toLocaleString('pt-BR', { day: '2-digit', timeZone });
  const monthStr = date.toLocaleString('pt-BR', { month: 'short', timeZone }).replace('.', '');
  return `${day} - ${monthStr.charAt(0).toUpperCase() + monthStr.slice(1)}`;
};

export const getPeriodLabel = (date: Date, type: string) => {
  if (type === 'day') return formatDateShortMonth(date);
  if (type === 'week') return `Sem ${Math.ceil(date.getDate() / 7)}`;
  if (type === 'month') return date.toLocaleDateString('pt-BR', { month: 'short' });
  if (type === 'year') return date.getFullYear().toString();
  return '';
};

export const getDynamicPeriodSeparation = (
  baseType: 'day' | 'week' | 'month' | 'year' | 'none',
  visibleMs: number,
  canvasWidth: number,
  minPixels: number = 40
): 'day' | 'week' | 'month' | 'year' | 'none' => {
  if (baseType === 'none') return 'none';
  if (visibleMs <= 0 || canvasWidth <= 0) return baseType;

  const msPerPixel = visibleMs / canvasWidth;
  const minMsRequired = msPerPixel * minPixels;

  const DAY_MS = 24 * 60 * 60 * 1000;
  const MONTH_MS = 30 * DAY_MS;
  const YEAR_MS = 365 * DAY_MS;

  const ranks = ['day', 'month', 'year'] as const;
  
  let baseIndex = ranks.indexOf(baseType as any);
  if (baseType === 'week') baseIndex = 1; // Map week to month for baseline promotion to avoid downgrade
  if (baseIndex === -1) return baseType;

  let targetIndex = 0;
  if (minMsRequired <= DAY_MS) targetIndex = 0; // day
  else if (minMsRequired <= MONTH_MS) targetIndex = 1; // month
  else targetIndex = 2; // year

  return ranks[Math.max(baseIndex, targetIndex)];
};

export const getSeparatorIndices = (data: any[], timeframe: string, overrideSepType?: 'day' | 'week' | 'month' | 'year' | 'none'): { index: number; label: string }[] => {
  if (!data || data.length < 2) return [];
  const sepType = overrideSepType || getTimeframeSeparation(timeframe);
  if (sepType === 'none') return [];

  const separators: { index: number; label: string }[] = [];
  
  for (let i = 1; i < data.length; i++) {
    const prevTs = data[i - 1].timestamp;
    const currTs = data[i].timestamp;
    if (!prevTs || !currTs) continue;

    let isNew = false;
    
    // Fast-path para 'day' (intervalos intradiários) para evitar new Date() no loop
    if (sepType === 'day') {
      const prevDay = Math.floor(prevTs / 86400000);
      const currDay = Math.floor(currTs / 86400000);
      isNew = currDay !== prevDay;
    } else {
      const prev = new Date(prevTs);
      const curr = new Date(currTs);
      
      if (sepType === 'week') {
        isNew = curr.getDay() < prev.getDay() || (curr.getTime() - prev.getTime() > 1000 * 60 * 60 * 24 * 6);
      } else if (sepType === 'month') {
        isNew = curr.getMonth() !== prev.getMonth();
      } else if (sepType === 'year') {
        isNew = curr.getFullYear() !== prev.getFullYear();
      }
    }

    if (isNew) {
      separators.push({
        index: i,
        label: getPeriodLabel(new Date(currTs), sepType)
      });
    }
  }

  return separators;
};
